import { DotsThree, Plus } from '@phosphor-icons/react';
import type { SocialAccountDto } from '@reelplicate/api-client/model';
import { useListPersonas } from '@reelplicate/api-client/personas';
import { getListPersonasQueryKey } from '@reelplicate/api-client/personas';
import {
  getListSocialAccountsQueryKey,
  useDeleteSocialAccount,
  useListSocialAccounts,
} from '@reelplicate/api-client/social-accounts';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import {
  Badge,
  BrandMark,
  type BrandMarkName,
  Button,
  IconButton,
  Menu,
  MenuContent,
  MenuDivider,
  MenuItem,
  MenuTrigger,
  Skeleton,
  toast,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useCheckAccount } from './check-account';
import { PLATFORM_LABELS } from './platforms';
import {
  ListRow,
  SettingsCard,
  SettingsPageLayout,
  SettingsSection,
} from './settings-layout';
import { SocialAccountModal } from './social-account-modal';

const MARKS: Record<SocialAccountDto['platform'], BrandMarkName> = {
  TIKTOK: 'tiktok',
  INSTAGRAM: 'instagram',
  YOUTUBE: 'youtube',
  LINKEDIN: 'linkedin',
  FACEBOOK: 'facebook',
};

const MORE_PLATFORMS: {
  platform: BrandMarkName;
  name: string;
  meta: string;
  comingSoon?: boolean;
}[] = [
  { platform: 'linkedin', name: 'LinkedIn', meta: 'Company pages and personal profiles' },
  { platform: 'x', name: 'X', meta: 'Video posts' },
  { platform: 'threads', name: 'Threads', meta: 'Video posts', comingSoon: true },
];

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * Figma "Settings — Connected accounts" (321:807): where Reelplicate
 * publishes, grouped by owner (the brand, then each persona), and platforms
 * not yet supported. Accounts are added by hand until the publishing
 * integrations bring real connections (and their sync and reconnect states).
 */
export function ConnectedAccountsPage() {
  const { data: workspaces } = useListWorkspaces();
  const workspace = workspaces?.[0];
  const workspaceId = workspace?.id ?? '';
  const enabled = { query: { enabled: Boolean(workspace) } };
  const { data: accounts } = useListSocialAccounts(workspaceId, enabled);
  const { data: personas } = useListPersonas(workspaceId, enabled);
  const [editing, setEditing] = useState<SocialAccountDto | 'new' | null>(null);
  const queryClient = useQueryClient();
  const remove = useDeleteSocialAccount();
  const check = useCheckAccount(workspaceId);
  const brand = workspace?.name ?? 'Your brand';

  const owners = [
    { id: null, name: brand, description: 'Brand accounts' },
    ...(personas?.items ?? []).map((p) => ({
      id: p.id,
      name: p.name,
      description: 'Persona accounts',
    })),
  ];
  const platformCount = new Set(accounts?.map((a) => a.platform)).size;

  const removeAccount = async (account: SocialAccountDto) => {
    try {
      await remove.mutateAsync({ id: workspaceId, accountId: account.id });
    } catch {
      return; // The failure is already shown as a toast.
    }
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: getListSocialAccountsQueryKey(workspaceId),
      }),
      queryClient.invalidateQueries({ queryKey: getListPersonasQueryKey(workspaceId) }),
    ]);
    toast.success(`${account.handle} removed`);
  };

  return (
    <SettingsPageLayout
      title="Connected accounts"
      description={
        accounts
          ? `Where Reelplicate publishes and what it reads for analytics. ${plural(accounts.length, 'account')} across ${plural(platformCount, 'platform')}.`
          : 'Where Reelplicate publishes and what it reads for analytics.'
      }
      actions={
        <Button
          leadingIcon={Plus}
          disabled={!workspace}
          onClick={() => setEditing('new')}
        >
          Add account
        </Button>
      }
    >
      {!accounts || !personas ? (
        <SettingsCard>
          <Skeleton className="my-4 h-10" />
        </SettingsCard>
      ) : (
        owners.map((owner) => {
          const owned = accounts.filter((a) => a.personaId === owner.id);
          return (
            <SettingsSection
              key={owner.id ?? 'brand'}
              title={owner.name}
              description={owner.description}
            >
              <SettingsCard>
                {owned.length === 0 ? (
                  <p className="text-body-sm text-text-secondary py-4">
                    No accounts yet.
                  </p>
                ) : (
                  owned.map((account) => {
                    const platform = PLATFORM_LABELS[account.platform];
                    return (
                      <ListRow
                        key={account.id}
                        tile={<BrandMark name={MARKS[account.platform]} size={20} />}
                        title={account.handle}
                        meta={platform}
                      >
                        <StatsBadge account={account} />
                        <Menu>
                          <MenuTrigger asChild>
                            <IconButton
                              icon={DotsThree}
                              size="sm"
                              variant="ghost"
                              aria-label={`More for ${account.handle} (${platform})`}
                            />
                          </MenuTrigger>
                          <MenuContent align="end">
                            <MenuItem onSelect={() => setEditing(account)}>
                              Change owner or handle
                            </MenuItem>
                            {account.platform !== 'LINKEDIN' && (
                              <MenuItem
                                disabled={check.pending}
                                onSelect={() => void check.run(account)}
                              >
                                Check again
                              </MenuItem>
                            )}
                            <MenuDivider />
                            <MenuItem
                              type="danger"
                              onSelect={() => void removeAccount(account)}
                            >
                              Remove
                            </MenuItem>
                          </MenuContent>
                        </Menu>
                      </ListRow>
                    );
                  })
                )}
              </SettingsCard>
            </SettingsSection>
          );
        })
      )}

      <SettingsSection
        title="More platforms"
        description="Connect once, then pick the owner (brand or persona) it posts for."
      >
        <SettingsCard>
          {MORE_PLATFORMS.map((p) => (
            <ListRow
              key={p.platform}
              tile={<BrandMark name={p.platform} size={20} />}
              title={p.name}
              meta={p.meta}
            >
              {p.comingSoon && <Badge>Coming soon</Badge>}
              <Button
                size="sm"
                variant="outline"
                disabled={p.comingSoon}
                aria-label={`Connect ${p.name}`}
              >
                Connect
              </Button>
            </ListRow>
          ))}
        </SettingsCard>
      </SettingsSection>
      {workspace && personas && (
        <SocialAccountModal
          workspaceId={workspace.id}
          brandName={brand}
          personas={personas.items}
          account={editing === 'new' ? undefined : (editing ?? undefined)}
          open={editing !== null}
          onOpenChange={(open) => !open && setEditing(null)}
        />
      )}
    </SettingsPageLayout>
  );
}

/**
 * Where an account's numbers stand. TikTok, Instagram, YouTube and Facebook
 * are read from public data by handle - no login to the account; LinkedIn has
 * no source yet.
 */
function StatsBadge({ account }: { account: SocialAccountDto }) {
  if (account.platform === 'LINKEDIN') {
    return <Badge>Added manually</Badge>;
  }
  switch (account.statsStatus) {
    case 'ACTIVE':
      return (
        <Badge
          tone="info"
          title="Read from public data by handle - no login to the account"
        >
          Public data
        </Badge>
      );
    case 'NOT_FOUND':
      return (
        <Badge tone="warning" title={account.statsError ?? undefined}>
          Handle not found
        </Badge>
      );
    case 'ERROR':
      return (
        <Badge tone="danger" title={account.statsError ?? undefined}>
          Sync failed
        </Badge>
      );
    default:
      return account.statsError ? (
        <Badge tone="warning" title={account.statsError}>
          Retrying
        </Badge>
      ) : (
        <Badge>Not synced yet</Badge>
      );
  }
}
